import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, AlertTriangle, Wallet, Plus, Trash2, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

// Gateways that can have multiple PG channels (e.g. ManaRecharge pay0/pay3/pay7).
const SUPPORTED_GATEWAYS = [
  { key: "manarecharge", label: "ManaRecharge", supports_channel: true },
  { key: "razorpay", label: "Razorpay", supports_channel: false },
  { key: "cashfree", label: "Cashfree", supports_channel: false },
  { key: "runpaisa", label: "Runpaisa", supports_channel: false },
];
const FEE_KEYS = ["mdr_flat", "mdr_percent", "settle_flat", "settle_percent"];
const KEY_LABELS = {
  mdr_flat: "MDR · Flat ₹",
  mdr_percent: "MDR · %",
  settle_flat: "Settlement · Flat ₹",
  settle_percent: "Settlement · %",
};

const blankRule = () => ({ mdr_flat: 0, mdr_percent: 0, settle_flat: 0, settle_percent: 0 });
const ruleIsAllZero = (r) => FEE_KEYS.every((k) => !Number(r?.[k] || 0));

// Normalize incoming feeMap to {gw: {channel: rule}} structure.
// Legacy flat {gw: {mdr_flat,...}} is mapped to {gw: {default: {...}}}.
function normalizeFees(feeMap) {
  const out = {};
  for (const [gw, entry] of Object.entries(feeMap || {})) {
    if (!entry || typeof entry !== "object") continue;
    // Detect legacy flat shape.
    if (FEE_KEYS.some((k) => k in entry)) {
      out[gw] = { default: { ...blankRule(), ...entry } };
    } else {
      const channels = {};
      for (const [ch, rule] of Object.entries(entry)) {
        channels[ch] = { ...blankRule(), ...(rule || {}) };
      }
      out[gw] = channels;
    }
  }
  return out;
}

// Convert back to API shape: if gw has only "default" channel and gateway doesn't
// support multiple channels, send flat; otherwise send nested.
function denormalizeFees(state) {
  const out = {};
  for (const [gw, channels] of Object.entries(state)) {
    if (!channels || Object.keys(channels).length === 0) continue;
    const meta = SUPPORTED_GATEWAYS.find((g) => g.key === gw);
    const keys = Object.keys(channels);
    if (!meta?.supports_channel && keys.length === 1 && keys[0] === "default") {
      // Legacy flat shape.
      out[gw] = channels.default;
    } else {
      out[gw] = channels;
    }
  }
  return out;
}

export default function AdminTopupFees() {
  // state shape: { gateway: { channel: rule } }
  const [fees, setFees] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ManaRecharge live channel catalogue (pay0/pay3/...) — refreshed on demand.
  const [manaChannels, setManaChannels] = useState([]);  // [{id, name, active}]
  const [manaChannelsError, setManaChannelsError] = useState("");
  const [manaChannelsLoading, setManaChannelsLoading] = useState(false);
  const [manaChannelsSource, setManaChannelsSource] = useState("");  // 'local' | 'relay' | ''

  const loadManaChannels = async () => {
    setManaChannelsLoading(true);
    setManaChannelsError("");
    try {
      const { data } = await api.get("/admin/payment-links/pg-channels");
      const list = (data.channels || []).map((c) => ({
        id: String(c.id || c.name || "").toLowerCase(),
        name: String(c.name || c.id || "").toUpperCase(),
        active: c.active !== false,
      })).filter((c) => c.id);
      setManaChannels(list);
      setManaChannelsSource(data.source || "");
      if (data.error) setManaChannelsError(data.error);
    } catch (err) {
      setManaChannelsError(formatApiError(err.response?.data?.detail) || "Failed to fetch live channels");
      setManaChannels([]);
      setManaChannelsSource("");
    } finally {
      setManaChannelsLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/topup-fees/master");
      setFees(normalizeFees(data.fees || {}));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load master fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-fetch ManaRecharge channels once the page loads — admin can pick from
  // the live list instead of typing channel ids by hand.
  useEffect(() => { loadManaChannels(); }, []);

  const setCell = (gw, ch, k, v) => {
    setFees((cur) => ({
      ...cur,
      [gw]: {
        ...(cur[gw] || {}),
        [ch]: {
          ...((cur[gw] || {})[ch] || blankRule()),
          [k]: Math.max(0, Number(v) || 0),
        },
      },
    }));
  };

  const enableGateway = (gw) => setFees((cur) => ({ ...cur, [gw]: cur[gw] || { default: blankRule() } }));
  const disableGateway = (gw) => setFees((cur) => {
    const { [gw]: _drop, ...rest } = cur;
    return rest;
  });

  const addChannel = (gw, channelId) => {
    const raw = (channelId || "").trim();
    if (!raw || !/^[a-z0-9_-]+$/.test(raw)) {
      toast.error("Channel id must be lowercase letters/digits/underscore (e.g. pay0, pay3)");
      return;
    }
    const id = raw;
    setFees((cur) => {
      const existing = cur[gw] || {};
      if (existing[id]) {
        toast.error(`Channel '${id}' already exists`);
        return cur;
      }
      return { ...cur, [gw]: { ...existing, [id]: blankRule() } };
    });
  };

  const removeChannel = (gw, ch) => {
    if (ch === "default") {
      toast.error("Cannot remove the 'default' channel — disable the gateway instead.");
      return;
    }
    setFees((cur) => {
      const { [ch]: _drop, ...rest } = cur[gw] || {};
      return { ...cur, [gw]: rest };
    });
  };

  // Validation surface.
  const invalidRows = useMemo(() => {
    const bad = [];
    for (const [gw, channels] of Object.entries(fees)) {
      if (!channels || Object.keys(channels).length === 0) {
        bad.push({ gw, channel: null, reason: "no channels" });
        continue;
      }
      for (const [ch, rule] of Object.entries(channels)) {
        if (ruleIsAllZero(rule)) bad.push({ gw, channel: ch, reason: "all-zero" });
      }
    }
    return bad;
  }, [fees]);
  const allRowsValid = invalidRows.length === 0;

  const save = async () => {
    if (!allRowsValid) {
      toast.error("Each enabled channel must have at least one non-zero fee.");
      return;
    }
    setSaving(true);
    try {
      const payload = denormalizeFees(fees);
      const { data } = await api.put("/admin/topup-fees/master", { fees: payload });
      setFees(normalizeFees(data.fees || {}));
      toast.success("Master top-up fees saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin · Top-up fees</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Master top-up fees (PG MDR + Settlement)
          </h1>
          <p className="text-muted-foreground mt-2 max-w-3xl text-sm">
            Default per-PG fees for wallet top-ups. Applied when a partner's price package has no
            explicit entry. <b>MDR</b> is added at the PG (customer pays extra).
            <b> Settlement</b> is deducted before crediting the wallet. ManaRecharge supports
            per-channel rates (e.g. <code className="font-mono">pay0</code>, <code className="font-mono">pay3</code>) —
            the <code className="font-mono">default</code> channel is used when no channel-specific
            rate is configured.
          </p>
        </div>
        <Button onClick={save} disabled={saving || loading || !allRowsValid} data-testid="save-master-fees">
          <Save className="h-4 w-4 mr-1" strokeWidth={1.5} />
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      {!allRowsValid && (
        <div
          className="mt-4 border border-destructive/40 bg-destructive/5 rounded-md p-3 text-sm flex items-start gap-2"
          data-testid="all-zero-warning"
        >
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" strokeWidth={1.5} />
          <span>One or more enabled channels have all-zero fees or empty configuration. Either set at least one value &gt; 0 or remove the channel/gateway.</span>
        </div>
      )}

      <div className="mt-6 grid gap-4">
        {SUPPORTED_GATEWAYS.map((g) => {
          const enabled = !!fees[g.key];
          const channels = fees[g.key] || {};
          return (
            <div key={g.key} className="surface-card p-5" data-testid={`gateway-row-${g.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-display text-lg font-semibold capitalize">{g.label}</span>
                  {g.supports_channel && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-mono">
                      multi-channel
                    </span>
                  )}
                  {enabled ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-success/15 text-success border border-success/30">
                      {Object.keys(channels).length} channel{Object.keys(channels).length === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                      Disabled
                    </span>
                  )}
                </div>
                {enabled ? (
                  <button
                    type="button"
                    className="text-xs text-destructive font-semibold underline-offset-2 hover:underline"
                    onClick={() => disableGateway(g.key)}
                    data-testid={`disable-${g.key}`}
                  >
                    Remove gateway
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-xs font-semibold underline-offset-2 hover:underline"
                    onClick={() => enableGateway(g.key)}
                    data-testid={`enable-${g.key}`}
                  >
                    Configure
                  </button>
                )}
              </div>

              {enabled && (
                <div className="mt-4 space-y-3">
                  {Object.entries(channels).map(([ch, rule]) => (
                    <ChannelEditor
                      key={ch}
                      gw={g.key}
                      channel={ch}
                      rule={rule}
                      onChange={(k, v) => setCell(g.key, ch, k, v)}
                      onRemove={() => removeChannel(g.key, ch)}
                      canRemove={ch !== "default"}
                      liveChannel={g.key === "manarecharge" ? manaChannels.find((mc) => mc.id === ch) : undefined}
                    />
                  ))}
                  {g.supports_channel && g.key === "manarecharge" && (
                    <ManaChannelPicker
                      configuredChannels={Object.keys(channels)}
                      liveChannels={manaChannels}
                      loading={manaChannelsLoading}
                      errorMsg={manaChannelsError}
                      source={manaChannelsSource}
                      onAdd={(id) => addChannel(g.key, id)}
                      onRefresh={loadManaChannels}
                    />
                  )}
                  {g.supports_channel && g.key !== "manarecharge" && (
                    <AddChannelInline onAdd={(id) => addChannel(g.key, id)} gw={g.key} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}

function ChannelEditor({ gw, channel, rule, onChange, onRemove, canRemove, liveChannel }) {
  const zero = ruleIsAllZero(rule);
  return (
    <div
      className={`rounded-md border p-3 ${zero ? "border-destructive/40 bg-destructive/5" : "border-border bg-muted/10"}`}
      data-testid={`channel-row-${gw}-${channel}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-background border border-border font-semibold">
            {channel}
          </span>
          {channel === "default" && (
            <span className="text-[10px] text-muted-foreground italic">used when no channel-specific rate matches</span>
          )}
          {liveChannel && liveChannel.active && (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-success/15 text-success border border-success/30"
              data-testid={`channel-live-active-${gw}-${channel}`}
            >
              <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
              Live · active
            </span>
          )}
          {liveChannel && !liveChannel.active && (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/30"
              data-testid={`channel-live-inactive-${gw}-${channel}`}
            >
              <XCircle className="h-3 w-3" strokeWidth={1.5} />
              Live · inactive
            </span>
          )}
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-destructive font-medium hover:underline inline-flex items-center gap-1"
            data-testid={`remove-channel-${gw}-${channel}`}
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
            Remove
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {FEE_KEYS.map((k) => (
          <div key={k}>
            <Label htmlFor={`${gw}-${channel}-${k}`} className="text-xs">{KEY_LABELS[k]}</Label>
            <Input
              id={`${gw}-${channel}-${k}`}
              data-testid={`fee-${gw}-${channel}-${k}`}
              type="number"
              min={0}
              step={k.endsWith("percent") ? "0.01" : "0.50"}
              value={rule[k] ?? 0}
              onChange={(e) => onChange(k, e.target.value)}
              className="mt-1 font-mono"
            />
          </div>
        ))}
      </div>
      {zero && (
        <div className="mt-2 text-[11px] text-destructive">All-zero is not allowed — set at least one fee &gt; 0 or remove this channel.</div>
      )}
    </div>
  );
}

function AddChannelInline({ onAdd, gw }) {
  const [val, setVal] = useState("");
  return (
    <div className="flex items-center gap-2 pt-1">
      <Input
        placeholder="e.g. pay0, pay3, pay7"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="font-mono text-sm h-8 max-w-[220px]"
        data-testid={`add-channel-input-${gw}`}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => { onAdd(val); setVal(""); }}
        data-testid={`add-channel-btn-${gw}`}
      >
        <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
        Add channel
      </Button>
    </div>
  );
}

// Smart picker for ManaRecharge — lists ALL live channels (active + inactive),
// lets admin add them with one tap. Admin can still manually type a custom
// channel id (useful when ManaRecharge adds new channels before we fetch them).
function ManaChannelPicker({ configuredChannels, liveChannels, loading, errorMsg, source, onAdd, onRefresh }) {
  const [manualVal, setManualVal] = useState("");
  const already = new Set((configuredChannels || []).map((c) => c.toLowerCase()));
  const available = (liveChannels || []).filter((c) => !already.has(c.id));

  return (
    <div className="rounded-md border border-dashed border-border bg-muted/5 p-3" data-testid="mana-channel-picker">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold">Live channels from ManaRecharge</span>
          <span className="text-[10px] text-muted-foreground font-mono">
            {loading ? "fetching…" : `${liveChannels.length} total · ${available.length} not yet configured`}
          </span>
          {source === "relay" && (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-mono"
              data-testid="mana-channel-source-relay"
              title="Local IP isn't whitelisted at ManaRecharge — channels fetched via Live Relay"
            >
              via Live relay
            </span>
          )}
          {source === "local" && (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-success/15 text-success border border-success/30 font-mono"
              data-testid="mana-channel-source-local"
            >
              direct
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline disabled:opacity-50"
          data-testid="refresh-mana-channels"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      {errorMsg && (
        <div className="text-[11px] text-destructive mb-2" data-testid="mana-channel-error">
          Couldn't fetch live channels — {errorMsg}. Add channels manually below.
        </div>
      )}

      {(liveChannels.length === 0 && !loading && !errorMsg) && (
        <div className="text-[11px] text-muted-foreground mb-2">
          ManaRecharge PG status returned no channels. Add manually if you know the channel ids.
        </div>
      )}

      {liveChannels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {liveChannels.map((c) => {
            const isAdded = already.has(c.id);
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => !isAdded && onAdd(c.id)}
                disabled={isAdded}
                className={[
                  "inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-mono transition-all",
                  isAdded
                    ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-60"
                    : c.active
                      ? "bg-success/10 text-success border-success/30 hover:bg-success/20"
                      : "bg-destructive/5 text-destructive border-destructive/30 hover:bg-destructive/10",
                ].join(" ")}
                data-testid={`mana-channel-pill-${c.id}`}
                title={isAdded ? "Already configured" : c.active ? "Active at PG · click to add" : "Inactive at PG · click to add (rate stored for future activation)"}
              >
                {c.active ? <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} /> : <XCircle className="h-3 w-3" strokeWidth={1.5} />}
                {c.name || c.id.toUpperCase()}
                {!isAdded && <Plus className="h-3 w-3" strokeWidth={1.5} />}
              </button>
            );
          })}
        </div>
      )}

      {/* Manual fallback — useful if ManaRecharge ships a new channel and the
          live API hasn't been refreshed yet, or when admin wants a custom id. */}
      <div className="flex items-center gap-2 pt-1 border-t border-border/40">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Manual add</span>
        <Input
          placeholder="e.g. pay9 (custom id)"
          value={manualVal}
          onChange={(e) => setManualVal(e.target.value)}
          className="font-mono text-sm h-8 max-w-[180px]"
          data-testid="add-channel-input-manarecharge"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => { onAdd(manualVal); setManualVal(""); }}
          data-testid="add-channel-btn-manarecharge"
        >
          <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
          Add
        </Button>
      </div>
    </div>
  );
}
